/* ================================================================ */
// SAVE COMPONENT | Save.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// Material UI
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialIcon from '@material-ui/lab/SpeedDialIcon';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import SvgIcon from '@material-ui/core/SvgIcon';
import SaveIcon from '@material-ui/icons/Save';
import makeStyles from '@material-ui/styles/makeStyles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        position:'relative',
        //width: '2.5rem',
        alignItems: 'flex-start'
    },
    button: {
        width: '2.5rem',
        height: '2.5rem',
        borderRadius: 0,
        boxShadow: 'none',
        '&.saved': {
            color: "#333",//theme.palette.action.saved.contrastText,
            backgroundColor: "#fff" //theme.palette.action.saved.main
        },
        '&.unsaved': {
            color: "#f00", //theme.palette.secondary.contrastText,
            backgroundColor: theme.palette.secondary.main
        }
    },
    actions: {
        position: 'absolute',
        top: '100%',
        margin: '0 !important',
        padding: '0 !important'
    },
    actionButton: {
        color: "#333",//theme.palette.secondary.contrastText,
        background: theme.palette.secondary.main,
        borderRadius: '0.5rem',
        margin: 0,
        marginTop: 8
    }
}));
/* ========================================== */
/**
 * Save Component
 * --------------
 * Save options menu for map state editing.
 * Displays icon and colour according to map editing
 * state (unsaved changes, already saved, etc)
 *
 * @component
 * @param {object} props component properties
 * @param {boolean} props.isSaved saved state of map
 * @param {boolean} props.isOpen initial menu open state
 * @param {boolean} props.isDialogOpen initial dialog open state
 * @param {string} props.filename loaded map state title
 * @param {function} props.onSave save trigger callback
 * @param {function} props.onSaveAs new save trigger callback with title
 */
const Save = ({
    filename,
    isSaved,
    isOpen,
    isDialogOpen,
    onSave,
    onSaveAs
}) => {
    /* ======================== */
    // PROPS
    /* ======================== */
    const classes = useStyles();
    const [open, setOpen] = useState(isOpen);
    const [dialogOpen, setDialogOpen] = useState(isDialogOpen);
    const [name, setName] = useState(filename);
    const fabClasses = classNames(
        classes.button,
        {
            'unsaved': !isSaved,
            'saved': isSaved
        }
    );
    const icon = isSaved ? <SpeedDialIcon openIcon={<SaveIcon />} icon={<SavedIcon />} /> : <SpeedDialIcon icon={<SaveIcon />} />;
    /* ======================== */
    // HANDLERS
    /* ======================== */
    /**
     * Handle Open
     * -----------
     * Triggers opened state of options menu
     */
    const handleOpen = () => {
        setOpen(true);
    };
    /**
     * Handle Close
     * ------------
     * Triggers closed state of options menu
     */
    const handleClose = () => {
        setOpen(false);
    };
    /**
     * Handle Toggle
     * -------------
     * Toggles open state of options menu
     */
    const handleToggle = () => {
        setOpen(!open);
    };
    /**
     * Handle Save
     * -----------
     * Triggers save action
     */
    const handleSave = () => {
        // Trigger save
        if(onSave) {
            onSave();
        }
    };
    /**
     * Handle Save As
     * --------------
     *  closed state of options menu
     */
    const handleSaveAs = () => {
        // Trigger save
        if(onSaveAs) {
            onSaveAs(name);
        }
        handleDialogClose();
    }
    const handleSaveDialog = () => {
        setDialogOpen(true);
    };
    const handleDialogClose = () => {
        setDialogOpen(false);
    };
    const handleTitleChange = (event) => {
        const value = event.target.value;
        setName(value);
    };
    return (
        <div className={classes.root}>
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle id="form-dialog-title">Save As</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        variant="outlined"
                        margin="dense"
                        id="data-filename"
                        label="Filename"
                        type="text"
                        value={name}
                        onChange={handleTitleChange}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveAs} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <SpeedDial
                ariaLabel="Save Options"
                classes={{
                    root: classes.root,
                    fab: fabClasses,
                    actions: classes.actions
                }}
                open={open}
                direction="left"
                icon={icon}
                onBlur={handleClose}
                onClick={handleToggle}
                onClose={handleClose}
                onFocus={handleOpen}
                onMouseEnter={handleOpen}
                onMouseLeave={handleClose}
            >
                <SpeedDialAction
                    className={classes.actionButton}
                    key="Save"
                    icon={<SaveIcon />}
                    tooltipTitle="Save"
                    tooltipPlacement="bottom"
                    tooltipOpen
                    onClick={handleSave}
                    disabled={isSaved}
                />
                <SpeedDialAction
                    className={classes.actionButton}
                    key="Save As"
                    icon={<SaveAsIcon />}
                    tooltipTitle="Save As"
                    tooltipPlacement="bottom"
                    tooltipOpen
                    onClick={handleSaveDialog}
                />
            </SpeedDial>
        </div>
    );
};
Save.propTypes = {
    isSaved: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool,
    isDialogOpen: PropTypes.bool,
    filename: PropTypes.string,
    // FUNCTIONS
    onSave: PropTypes.func,
    onSaveAs: PropTypes.func
};
Save.defaultProps = {
    isOpen: false,
    isDialogOpen: false,
    filename: ''
};
const SavedIcon = (props) => {
    return (
        <SvgIcon {...props}>
            <path fillRule="evenodd" clipRule="evenodd" d="M5 3H17L21 7V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3ZM5 9H15V5H5V9ZM6.11364 13.2537L9.45455 16.7015L17.8864 8L19 9.14925L9.45455 19L5 14.403L6.11364 13.2537Z" />
        </SvgIcon>
    );
};
const SaveAsIcon = (props) => {
    return (
        <SvgIcon {...props}>
            <path fillRule="evenodd" clipRule="evenodd" d="M4 4C4 2.9 4.89 2 6 2H18L22 6V18C22 19.1 21.1 20 20 20C20 21.1 19.1 22 18 22H4C2.89 22 2 21.1 2 20V6C2 4.9 2.89 4 4 4ZM6 3H17.5858L21 6.41421V18C21 18.5477 20.5477 19 20 19V8L16 4H5C5 3.44903 5.44554 3 6 3ZM4 6V10H14V6H4ZM8.76168 19C8.28772 18.4697 8 17.7692 8 17C8 15.34 9.34 14 11 14C12.66 14 14 15.34 14 17C14 17.7692 13.7123 18.4697 13.2383 19C12.6894 19.6141 11.8908 20 11 20C10.1092 20 9.31056 19.6141 8.76168 19Z" />
        </SvgIcon>
    );
};
/* ========================================== */
// Export
/* ========================================== */
export default Save;